/**
 * groupMetaInfoByConnection — group MetaInfo records by connection,
 * resolving each connection's display name from the saved connection list
 * (alias > server > raw connectionId fallback). Records with no
 * declarations are filtered out, databases inside a group are sorted
 * alphabetically, and groups themselves are sorted by display name.
 */

import type { Connection, DatabaseMetaInfo } from '@/types/electron';
import { safeConnectionName } from '@/types/safeConnectionDisplay';

export type MetaInfoConnectionGroup = {
  connectionId: string;
  displayName: string;
  databases: DatabaseMetaInfo[];
};

const hasEntries = (mi: DatabaseMetaInfo) =>
  mi.looseRelationships.length > 0 || mi.jsonColumns.length > 0;

export function groupMetaInfoByConnection(
  records: DatabaseMetaInfo[],
  connections: Connection[],
): MetaInfoConnectionGroup[] {
  const byConn = new Map<string, DatabaseMetaInfo[]>();
  for (const mi of records.filter(hasEntries)) {
    const arr = byConn.get(mi.connectionId) ?? [];
    arr.push(mi);
    byConn.set(mi.connectionId, arr);
  }
  const out: MetaInfoConnectionGroup[] = [];
  for (const [connectionId, dbs] of byConn) {
    const conn = connections.find((c) => c.id === connectionId);
    const displayName = safeConnectionName(conn, connectionId);
    dbs.sort((a, b) => a.database.localeCompare(b.database));
    out.push({ connectionId, displayName, databases: dbs });
  }
  out.sort((a, b) => a.displayName.localeCompare(b.displayName));
  return out;
}
